"use client"

import { useState } from "react"
import { createRoute } from "@tanstack/react-router"

import { EmailSignaturesSettings } from "@/components/email-signatures-settings"
import { PageHeader } from "@/components/graphite/page-header"
import { RecordTabs } from "@/components/graphite/record-tabs"
import { TabHeader, TabSurface } from "@/components/graphite/surface"
import { InviteUserDialog } from "@/components/invite-user-dialog"
import { ManageOrg } from "@/components/manage-organization"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useAuth } from "@/hooks/useAuth"
import MainAppRoute from "@/rootRoutes/MainApp"

const SettingsRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/settings",
  component: SettingsPage,
})
export default SettingsRoute

const TABS = ["Profile", "Team", "Signatures"] as const
type Tab = (typeof TABS)[number]

/**
 * One fact about the signed-in person, read-only.
 *
 * `/auth/user` is a GET and nothing writes a profile back, so an editable field
 * here could only have pretended to save. This tab used to carry a job title,
 * an organization and a phone number that were hard-coded strings — "Senior
 * Technical Recruiter" at "TechCorp Inc." — behind an Edit button whose Save
 * closed the form and stored nothing.
 */
function ProfileField({
  label,
  value,
  loading,
}: {
  label: string
  value?: string
  loading: boolean
}) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] leading-none text-muted-foreground">{label}</p>
      {loading ? (
        /*
          A dash where a name goes is a claim that the account has no name. It
          is the wrong claim to make while the request is still in flight, which
          is most of the time this page is first looked at.
        */
        <div className="mt-2 h-[13px] w-32 animate-pulse rounded bg-foreground/10" />
      ) : (
        <p className="mt-1.5 truncate text-[12.5px] font-medium leading-[1.3]">
          {value?.trim() || "—"}
        </p>
      )}
    </div>
  )
}

function ProfileTab() {
  const { user, isLoading } = useAuth()
  const initial = (user?.name?.trim()?.[0] ?? user?.email?.trim()?.[0] ?? "U").toUpperCase()

  return (
    <TabSurface fill={false}>
      <TabHeader label="Profile" />
      <div className="flex flex-col gap-5 pt-4 @lg:flex-row @lg:items-center @lg:gap-7">
        <Avatar className="size-14 flex-none border @2xl:size-16">
          <AvatarImage src={user?.picture ?? undefined} alt="" />
          <AvatarFallback className="text-[15px]">{initial}</AvatarFallback>
        </Avatar>
        <div className="grid min-w-0 gap-4 @sm:grid-cols-2 @lg:gap-9">
          <ProfileField label="Full name" value={user?.name} loading={isLoading} />
          <ProfileField label="Email" value={user?.email} loading={isLoading} />
        </div>
      </div>
      <p className="mt-5 border-t pt-3.5 text-[11.5px] leading-[1.5] text-muted-foreground">
        Your name, email and picture come from the account you signed in with, and
        are changed there.
      </p>
    </TabSurface>
  )
}

/**
 * Settings — three tabs, each backed by something.
 *
 * The page wears the same chrome as every other one: `PageHeader` for the band,
 * `RecordTabs` for the strip under it, and the body scrolling inside a page
 * that owns its own height. It had been a hand-written `text-3xl` heading over
 * stock shadcn tabs and `md:` viewport padding — which is the wrong axis here,
 * since the ATS pane is often narrow while the window is wide, so a page that
 * asks the viewport lays itself out for a width it does not have.
 */
function SettingsPage() {
  const [tab, setTab] = useState<Tab>("Profile")
  const [inviteOpen, setInviteOpen] = useState(false)

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <PageHeader title="Settings" description="Your account, your team, and how you sign off">
        <RecordTabs tabs={TABS} value={tab} onChange={setTab} />
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 @2xl:px-[26px] @2xl:py-5">
        {tab === "Profile" && <ProfileTab />}
        {tab === "Team" && <ManageOrg inviteTrigger={() => setInviteOpen(true)} />}
        {tab === "Signatures" && <EmailSignaturesSettings />}
      </div>

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  )
}
